import React, {
	CSSProperties,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./tab-tag-general.module.scss";
import { mapboxStyleID, mapboxUsername } from "../../constants";
import { VelavuDevice } from "velavu-js-api";
import DynamicBattery from "../../elements/dynamic-battery";
import VelavuSimpleTable, {
	CenteredTD,
	MultilineTD,
} from "../../elements/velavu-simple-table";
import { useDisplayLocation, useMapboxAPIKey } from "../../helper/api-helper";
import { classArr } from "../../helper/style-helper";
import NoLocation from "../../img/no-location.png";
import { mapDeviceSymbolURLForDevice } from "../../helper/map-device-symbols";
import DetailedStatusRow from "../../elements/detailed-status-row";
import LocationDescription from "../dashboard/dropdown/location-description";

const mapboxZoom = 16;

export default function TabTagGeneral(props: {
	style?: CSSProperties;
	className?: string;
	tag: VelavuDevice;
	onClickMap?: () => void;
}) {
	const { t } = useTranslation();
	const coordinates = props.tag.location?.coordinates;
	const assetLocation = useDisplayLocation(props.tag);
	const mapboxKey = useMapboxAPIKey();
	const mapboxMapURL =
		coordinates === undefined
			? undefined
			: `https://api.mapbox.com/styles/v1/${mapboxUsername}/${mapboxStyleID}/static/${coordinates[0]},${coordinates[1]},${mapboxZoom},0,0/600x400?access_token=${mapboxKey}&@2x=true`;

	const [height, setHeight] = useState(0);
	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (ref.current) {
			setHeight(ref.current.clientHeight);
		}
	}, [ref]);

	const mapDeviceIndicatorURL = useMemo(() => {
		return mapDeviceSymbolURLForDevice(props.tag);
	}, [props.tag]);

	return (
		<div
			style={props.style}
			className={`${props.className ?? ""} ${styles.sectionGeneral}`}
		>
			<div className={`${styles.sectionGeneralItem}`} ref={ref}>
				<VelavuSimpleTable>
					<tbody>
						<tr>
							<td>{t("asset_detail.tag.tag_id")}</td>
							<td>{props.tag.id}</td>
						</tr>
						<tr>
							<td>{t("asset_detail.tag.location")}</td>
							<MultilineTD>
								<LocationDescription
									device={props.tag}
									textClassName={null}
								/>
							</MultilineTD>
						</tr>
						<tr>
							<td>{t("asset_detail.tag.status")}</td>
							<td>
								<DetailedStatusRow device={props.tag} />
							</td>
						</tr>
						{props.tag.state?.power && (
							<tr>
								<td>{t("asset_detail.tag.battery")}</td>
								<CenteredTD>
									<span style={{ marginRight: 4 }}>
										{props.tag.state?.power
											?.battery_level !== undefined
											? `${props.tag.state.power.battery_level}%`
											: t("common.na")}
									</span>
									<DynamicBattery
										resource={props.tag.state?.power}
									/>
								</CenteredTD>
							</tr>
						)}
						{props.tag.state && (
							<tr>
								<td>{t("asset_detail.tag.firmware")}</td>
								<MultilineTD>
									{props.tag.state.app_version}
								</MultilineTD>
							</tr>
						)}
						<tr>
							<td>{t("asset_detail.tag.serial_code")}</td>
							<td>{props.tag.serial_code}</td>
						</tr>
					</tbody>
				</VelavuSimpleTable>
			</div>

			<div className={styles.sectionGeneralSeparator} />
			{mapboxMapURL && (
				<div
					className={classArr(
						styles.sectionGeneralItem,
						styles.mapContainer,
						props.onClickMap && styles.clickableMapContainer,
					)}
					style={{ height: height }}
					onClick={props.onClickMap}
				>
					<img
						className={styles.mapIndicator}
						src={mapDeviceIndicatorURL}
						alt={props.tag.id}
					/>
					<img
						className={styles.mapImage}
						style={{ height: height }}
						src={mapboxMapURL}
						alt={assetLocation}
					/>
				</div>
			)}
			{!mapboxMapURL && (
				<div
					className={`${styles.sectionGeneralItem} ${styles.mapContainer}`}
					style={{ height: height }}
				>
					<div className={styles.textWrapper}>
						<span className={styles.noLocationText}>
							{t("asset_detail.tag.no_location_data")}
						</span>
					</div>
					<img
						className={`${styles.mapImage} ${styles.noLocation}`}
						style={{ height: height }}
						src={NoLocation}
						alt={t("asset_detail.tag.no_location_data")}
					/>
				</div>
			)}
		</div>
	);
}
